import { useEffect, useReducer, useState } from 'react';
import type { ReactNode } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { PlayIcon } from '@hugeicons/core-free-icons';
import { usePostHog } from '@posthog/react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../../api/http';
import { Button } from '../../components/Button';
import { Turnstile, turnstileEnabled } from '../../components/Turnstile';
import type { Endpoint } from '../../features/catalog/model';
import { publicTrySchemaQueryOptions } from '../../features/execution/queries';
import { runPublicTry } from '../../features/execution/repository';
import { publicToolError } from '../../features/public-tools/publicToolError';
import { requestHasStatus } from '../../features/requestError';
import { sampleCoverage } from '../../lib/endpointFields';
import { RequestSnippets } from '../../features/request-console/RequestSnippets';
import { SchemaForm } from '../../features/request-console/SchemaForm';
import { missingFieldsMessage, useSchemaForm } from '../../features/request-console/useSchemaForm';
import { TryResponsePane, type TryPhase } from '../../features/request-console/TryResponsePane';
import { useResourceLifetime } from '../../hooks/useResourceLifetime';
import { SampleCoverageNote } from './SampleCoverageNote';
import { SkuCallCard } from './SkuCallCard';
import { TryRunCta } from './TryRunCta';
import { m } from '../../paraglide/messages';

export interface PublicTryConsoleProps {
  endpoint: Endpoint;
  /** Platform plus action, e.g. "Instagram Profile". The endpoint alone knows
   *  only its action ("Profile"), so the page supplies the readable name. */
  title?: string;
  /** True on the public site, where a working free run is the moment the signup
   *  is earned. False inside the dashboard: the reader already has a wallet, so
   *  the run CTA is not rendered and the cap note points at the Playground
   *  instead of a signup. */
  acquisition: boolean;
}

// Run flow: validate -> run -> done | error.
interface RunState {
  phase: TryPhase;
  output: unknown;
  error: string;
  showErrors: boolean;
}
type RunAction =
  | { type: 'reset' }
  | { type: 'validationFailed'; message: string }
  | { type: 'running' }
  | { type: 'succeeded'; output: unknown }
  | { type: 'failed'; message: string };

const initialRun: RunState = {
  phase: 'idle',
  output: null,
  error: '',
  showErrors: false,
};

function runReducer(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case 'reset':
      return initialRun;
    case 'validationFailed':
      return {
        phase: 'error',
        output: null,
        error: action.message,
        showErrors: true,
      };
    case 'running':
      return { ...state, phase: 'running', error: '' };
    case 'succeeded':
      return { ...state, phase: 'done', output: action.output };
    case 'failed':
      return { ...state, phase: 'error', output: null, error: action.message };
    default:
      return state;
  }
}

/**
 * The "Make your first request" console on a public API page. It loads the SKU's
 * schema (GET /public/try/{sku}/schema, no key), then always shows the live
 * cURL/Node/Python request snippet and the response shape side by side, prefilled
 * from the schema example. For a try-eligible SKU it additionally renders an editable
 * form and a "Run it" button that executes the request house-funded against POST
 * /public/try/{sku} - the same multi-provider failover as prod, gated by Cloudflare
 * Turnstile (a no-op in dev). The snippet shows the discovered paid operation
 * (with the $ANYAPI_KEY placeholder), so what you copy is what you'd ship. If the
 * schema can't load (e.g. an older gateway), it falls back to the static SKU card.
 */
export const PublicTryConsole = ({ endpoint, title, acquisition }: PublicTryConsoleProps) => {
  const sku = endpoint.sku;
  const interactive = Boolean(endpoint.tryEligible);
  const schemaRequest = useQuery(publicTrySchemaQueryOptions(sku));
  const { runIfCurrent } = useResourceLifetime(sku);
  // Data wins over error. These pages now ship the schema in their HTML, and the
  // browser refetches it on mount because the build stamped it hours or days
  // ago. Reading the error first meant one failed refetch replaced a fully
  // rendered console with the bare SKU card - the reader watching content
  // vanish on hydration, having already been served the real thing.
  const schemaStatus = schemaRequest.data ? 'ready' : schemaRequest.error ? 'error' : 'loading';
  const [values, setValues] = useState<Record<string, string>>({});
  const form = useSchemaForm(schemaRequest.data, values);
  const { fields, prefilled, input, missing } = form;
  const [token, setToken] = useState<string | null>(turnstileEnabled ? null : '');
  const [runState, dispatchRun] = useReducer(runReducer, initialRun);
  const posthog = usePostHog();

  // Reload the schema and reset all run state whenever the resolved SKU changes.
  useEffect(() => {
    if (!sku) return;
    dispatchRun({ type: 'reset' });
    setToken(turnstileEnabled ? null : '');
    setValues(schemaRequest.data ? prefilled : {});
  }, [sku, schemaRequest.data, schemaRequest.error, prefilled]);

  // The gateway advertises the exact cap for eligible list operations. A missing
  // value means there is no item cap, so the UI never guesses from schema shape.
  const tryMaxItems = interactive ? endpoint.tryMaxItems : undefined;

  const ready = token !== null;

  // A hand-written sample wins over the one synthesized from the output schema.
  // The synthesized one is schema-complete; the hand-written one is often a
  // cut-down illustration, so it says how much of the response it shows.
  const sample = endpoint.sampleResponse || form.sample;
  const coverage = endpoint.sampleResponse
    ? sampleCoverage(schemaRequest.data?.outputSchema, endpoint.sampleResponse)
    : null;

  const onChange = (name: string, value: string) => setValues((v) => ({ ...v, [name]: value }));

  const run = async () => {
    if (missing.length > 0) {
      dispatchRun({
        type: 'validationFailed',
        message: missingFieldsMessage(missing),
      });
      return;
    }
    dispatchRun({ type: 'running' });
    posthog?.capture('public_try_run', { sku });
    try {
      const res = await runPublicTry(sku, input, token ?? '');
      runIfCurrent(() => dispatchRun({ type: 'succeeded', output: res }));
    } catch (e) {
      runIfCurrent(() => {
        if (requestHasStatus(e, 403)) setToken(turnstileEnabled ? null : '');
        dispatchRun({ type: 'failed', message: publicToolError(e) });
      });
    }
  };

  if (!sku) return null;

  // No public schema (e.g. an older gateway): fall back to the static SKU card so the
  // cURL + sample response still render.
  if (schemaStatus === 'error') return <SkuCallCard endpoint={endpoint} />;
  if (schemaStatus === 'loading') {
    return (
      <div className="rounded-12 border border-rule bg-wash px-4 py-10 text-center">
        <p className="mono text-13 text-text-3">{m.endpoint_try_loading()}</p>
      </div>
    );
  }

  // For a try-eligible SKU the request card gains a default "Input" tab (the form)
  // and a footer with the bot check + Run button; a non-eligible SKU shows the
  // language tabs only (read-only). `key={sku}` resets the active tab per endpoint.
  const formTab = interactive ? (
    <SchemaForm
      fields={fields}
      values={values}
      onChange={onChange}
      missing={runState.showErrors ? missing : []}
      prefilled={prefilled}
    />
  ) : undefined;
  const footer = interactive ? (
    <>
      {turnstileEnabled ? <Turnstile onVerify={setToken} onReset={() => setToken(null)} /> : <span />}
      <Button variant="primary" onClick={run} disabled={runState.phase === 'running' || !ready}>
        <HugeiconsIcon icon={PlayIcon} strokeWidth={1.5} className="size-3.5" />
        {runState.phase === 'running' ? m.endpoint_try_running() : m.endpoint_try_run()}
      </Button>
    </>
  ) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid items-stretch gap-4 lg:grid-cols-2">
        <RequestSnippets
          key={sku}
          baseUrl={API_BASE_URL}
          sku={sku}
          operation={endpoint}
          input={input}
          title={title}
          agentActions
          /* A signed-in reader already has keys; sending them to /sign-up for
             one is the same defect as the run CTA above. */
          keySource={acquisition ? 'signup' : 'account'}
          inputTab={formTab}
          footer={footer}
        />
        <TryResponsePane
          phase={runState.phase}
          output={runState.output}
          sample={sample}
          error={runState.error}
          notice={
            <>
              {noticeFor(runState.phase, runState.output, acquisition, tryMaxItems)}
              {coverage && runState.phase !== 'done' ? <SampleCoverageNote coverage={coverage} /> : null}
            </>
          }
        />
      </div>

      {/* Only after a run that actually returned data. A failed run has nothing
          to sell, and an empty result already explains itself in the pane above -
          asking for a signup on top of either reads as a paywall on a broken
          tool. Full width, directly under the response, because a visitor
          reading their own data is the one moment the ask is earned. */}
      {acquisition && runState.phase === 'done' && !isEmptyResult(runState.output) ? (
        <TryRunCta endpoint={endpoint} />
      ) : null}
    </div>
  );
};

// A prominent callout sitting directly on top of the JSON for a capped-list
// endpoint: the free run returns only the gateway-advertised item count, so a short
// response reads as capped, not as the whole dataset. A light-blue tinted band
// (accent tokens, on-brand) with the key phrase bold, set off from the JSON by a
// clear border. Copy is tense-neutral so it reads over both the sample and a
// live result.
const CapNotice = ({ maxItems, acquisition }: { maxItems: number; acquisition: boolean }) => (
  <div className="border-b border-accent-line bg-accent-soft px-4 py-2.5 text-13 leading-snug text-accent">
    <ParaglideMessage
      message={acquisition ? m.endpoint_try_cap_signup : m.endpoint_try_cap_sample}
      inputs={{ count: maxItems }}
      markup={{ b: ({ children }) => <span className="font-semibold text-text">{children}</span> }}
    />
  </div>
);

// Shown when a run succeeds (HTTP 200) but the normalized envelope carries no
// data - e.g. `{found:false}` for a handle that does not exist. Without it, an
// empty `{"found":false,"data":null}` body reads as a broken tool rather than a
// legitimate "nothing matched". Kept neutral (grey), not the accent callout.
const EmptyNotice = () => (
  <p className="border-b border-line-2 px-4 py-2.5 text-13 text-text-2">{m.endpoint_try_empty()}</p>
);

// noticeFor picks the note rendered above the JSON. An empty result always
// explains itself and takes precedence (done phase only); otherwise a
// capped-list endpoint shows the free-run cap callout over both its pre-run
// sample and a live result.
function noticeFor(
  phase: TryPhase,
  output: unknown,
  acquisition: boolean,
  tryMaxItems?: number,
): ReactNode {
  if (phase === 'done' && isEmptyResult(output)) return <EmptyNotice />;
  if (tryMaxItems !== undefined) return <CapNotice maxItems={tryMaxItems} acquisition={acquisition} />;
  return undefined;
}

// resultList extracts the primary result array from the normalized envelope, or
// null when the output is a single object (nothing a per-item cap can truncate).
// Handles a bare array, `{data: [...]}`, and a nested list under the common keys.
function resultList(output: unknown): unknown[] | null {
  if (output == null || typeof output !== 'object') return null;
  const env = output as Record<string, unknown>;
  const data = 'data' in env ? env.data : env;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const rec = data as Record<string, unknown>;
    for (const key of ['items', 'posts', 'results', 'rows', 'data']) {
      if (Array.isArray(rec[key])) return rec[key] as unknown[];
    }
  }
  return null;
}

// isEmptyResult reports whether a successful run returned no usable data, across
// the normalized envelope shapes: `found:false`, a null/absent `data`, or an
// empty list under the common list keys. A populated object or non-empty list is
// never empty.
function isEmptyResult(output: unknown): boolean {
  if (output == null) return true;
  if (typeof output !== 'object') return false;
  const env = output as Record<string, unknown>;
  if (env.found === false) return true;
  const data = 'data' in env ? env.data : env;
  if (data == null) return true;
  const list = resultList(output);
  if (list) return list.length === 0;
  return false;
}

